
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Clock } from 'lucide-react';

type User = {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Editor' | 'Viewer';
  status: 'Active' | 'Inactive';
  lastActive: string;
  invitationStatus?: 'pending' | 'accepted' | 'expired';
  invitedAt?: string;
  invitedBy?: string;
};

interface ResendInvitationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  onInvitationResent: () => void;
}

export function ResendInvitationDialog({ 
  open, 
  onOpenChange, 
  user, 
  onInvitationResent 
}: ResendInvitationDialogProps) {
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);

  const handleResendInvitation = async () => {
    try {
      setIsResending(true);
      
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        throw new Error('You must be logged in to resend invitations');
      }

      const { data, error } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: user.email,
          fullName: user.name,
          role: user.role.toLowerCase(),
          invitedBy: currentUser.id,
          isResend: true
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: `Invitation resent to ${user.email}`,
      });

      onInvitationResent();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to resend invitation",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Resend Invitation
          </DialogTitle>
          <DialogDescription>
            Send a new invitation email to this user with a fresh access link.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-8 w-8 items-center justify-center bg-primary/10 text-xs font-medium uppercase text-primary rounded-full">
                {user.name.substring(0, 2)}
              </div>
              <div>
                <p className="font-medium">{user.name}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Role: {user.role}</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>What happens when you resend:</strong>
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• A new invitation email will be sent</li>
              <li>• The user will receive a fresh setup link</li>
              <li>• Previous invitation links will remain valid</li>
              <li>• The invitation timestamp will be updated</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isResending}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResendInvitation}
            disabled={isResending}
          >
            {isResending ? 'Resending...' : 'Resend Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
